import React, { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MainContextState, MainContext } from "../../context/MainContext";
import {
    Button,
    Checkbox,
    FormControl,
    InputLabel,
    MenuItem,
    Pagination,
    Paper,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNotification } from "../../hooks/useNotification";
import Loader from "../../components/Loader/Loader";
import CustomSnackbar from "../../components/UI/CustomSnackbar";
import { MODAL_TYPE } from "../../constants/common";
import { PostDataCreate } from "../../types/api/post";
import { createNewReport, deleteReport, editReport, getReport, getReports } from "../../api/reportAPI";
import Modal from "../../components/UI/Modal/Modal";
import { ReportControlPointData, ReportCreate, ReportData, ReportState } from "../../types/api/report";
import { format } from "date-fns";
import ru from "date-fns/locale/ru";
import { LinkBehavior } from "../../components/LinkBehavior/LinkBehavior";
import { ReportCreateForm } from "../../components/forms/ReportForm/ui/ReportCreateForm";
import { getReportControlPoints } from "../../api/reportControlPointAPI";
import { getDateFormat } from "../../utils/getDateFormat";
import CustomButton from "../../components/UI/CustomButton/CustomButton";
import dayjs from "dayjs";

export const ReportSchedulePage = () => {
    const isAdmin = localStorage.getItem("admin_role") === "true";
    const { constants } = useContext<Partial<MainContextState>>(MainContext);
    const { id } = useParams();
    const isOwner =
        localStorage.getItem("employee_type") == "16" &&
        localStorage.getItem("employee_center_id") == localStorage.getItem("current_center_id");
    const [load, setLoad] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
    });
    const [reports, setReports] = useState<Array<ReportData>>();
    const [reportDates, setReportsDates] = useState<Array<ReportControlPointData>>();
    const [reportId, setReportId] = useState(0);
    const [reportDateId, setReportDateId] = useState(0);
    const [deleteReportId, setDeleteReportId] = useState(0);
    const [reportState, setReportState] = useState("0");
    const { setNotification, setOpenSnackbar, notification, openSnackbar } = useNotification();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [currentModalType, setCurrentModalType] = useState("edit");
    const closeModal = () => {
        setShowModal(false);
    };
    const createForm = useForm<ReportCreate>({
        defaultValues: {
            info: [],
            report_date_id: null,
            status: { message: "", state: 0 },
        },
    });

    const editForm = useForm<ReportCreate>({
        defaultValues: {
            report_date_id: null,
            status: { message: "", state: 0 },
        },
    });

    const fetchReports = () => {
        setLoad(true);
        getReports(
            Number(id),
            ({ data, last_page, current_page }) => {
                setReports(data);
                setPagination({ ...pagination, last_page: last_page, current_page: current_page });
                setLoad(false);
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setLoad(false);
            },
            { page: pagination.current_page, state: reportState },
        );
    };

    const editResetFormData = (data: ReportData) => {
        editForm.reset({
            status: {
                message: data.status?.message ?? "",
                state: data.status?.state ?? 0,
            },
        });
    };

    const getReportData = (reportId: number) => {
        setLoad(true);
        getReport(
            Number(id),
            Number(reportId),
            ({ data }) => {
                editResetFormData(data);
                setLoad(false);
            },
            err => {
                setLoad(false);
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
            },
        );
    };

    const create: SubmitHandler<ReportCreate> = (data: ReportCreate) => {
        setLoad(true);

        createNewReport(
            Number(id),
            data,
            () => {
                fetchReports();
                setNotification({
                    severity: "success",
                    message: "Отчен создан успешно",
                });
                setOpenSnackbar(true);
                setLoad(false);
                closeModal();
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setOpenSnackbar(true);
                setLoad(false);
                Object.keys(err?.errors).forEach((fieldName: any) => {
                    createForm.setError(fieldName, {
                        type: "custom",
                        message: err?.errors[fieldName][0],
                    });
                });
            },
        );
    };

    const edit = (data: ReportCreate) => {
        setLoad(true);
        editReport(
            Number(id),
            Number(reportId),
            { ...data, report_date_id: reportDateId },
            ({ data }) => {
                editResetFormData(data);
                fetchReports();
                setNotification({
                    severity: "success",
                    message: "Отчет изменен успешно",
                });
                setOpenSnackbar(true);
                setLoad(false);
                closeModal();
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setLoad(false);
                setOpenSnackbar(true);
                if (err?.errors) {
                    Object.keys(err.errors).forEach((fieldName: any) => {
                        editForm.setError(fieldName, {
                            type: "custom",
                            message: err.errors[fieldName][0],
                        });
                    });
                }
            },
        );
    };

    const del: SubmitHandler<any> = () => {
        setLoad(true);
        deleteReport(
            Number(id),
            deleteReportId,
            () => {
                fetchReports();
                setNotification({
                    severity: "success",
                    message: "Центр удален успешно",
                });
                setOpenSnackbar(true);
                setLoad(false);
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setOpenSnackbar(true);
                setLoad(false);
            },
        );
        closeModal();
    };

    const createContent = (
        <FormProvider {...createForm}>
            <ReportCreateForm reportDates={reportDates ?? []} />
        </FormProvider>
    );

    const editContent = (
        <FormProvider {...editForm}>
            <ReportCreateForm reportDates={reportDates ?? []} editForm />
        </FormProvider>
    );

    const deleteContent = <div>Вы уверены, что хотите удалить данные об отчете?</div>;

    const modalType = {
        [MODAL_TYPE.create]: {
            title: "Создание отчета",
            onSubmit: createForm.handleSubmit(create),
            content: createContent,
        },
        [MODAL_TYPE.edit]: {
            title: "Изменение статуса отчета",
            onSubmit: editForm.handleSubmit(edit),
            content: editContent,
        },
        [MODAL_TYPE.delete]: {
            title: "Удаление центра",
            onSubmit: del,
            content: deleteContent,
        },
    };

    useEffect(() => {
        fetchReports();
    }, [pagination.current_page]);

    useEffect(() => {
        if (id && reportId) {
            getReportData(Number(reportId));
        }
    }, [id, reportId]);

    useEffect(() => {
        getReportControlPoints(
            ({ data }) => {
                setReportsDates(data);
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
            },
            { per_page: -1, from: dayjs().format("YYYY/MM/DD") },
        );
    }, []);

    return (
        <div className={"container"}>
            <div className={"pageHeader"}>Отчеты</div>
            <div
                style={{
                    display: "flex",
                    gridGap: "10px",
                    alignItems: "center",
                }}>
                <FormControl
                    sx={{
                        marginTop: "16px",
                        marginBottom: "8px",
                        width: "360px",
                    }}>
                    <InputLabel id={"resident-type-label"}>Статус</InputLabel>
                    <Select
                        size={"small"}
                        labelId={"report-state"}
                        label={"Статус"}
                        onChange={(event: SelectChangeEvent) => {
                            setReportState(event.target.value);
                        }}
                        value={reportState}>
                        {constants?.report_state.map(type => (
                            <MenuItem key={type.value} value={type.value}>
                                {type.text}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <CustomButton
                    sx={{
                        marginTop: "4px",
                    }}
                    onClick={fetchReports}>
                    Поиск
                </CustomButton>
            </div>
            <TableContainer component={Paper} sx={{ marginBottom: "80px" }}>
                <Table aria-label="centers table" sx={{ "& td": { border: "none" } }}>
                    <TableHead sx={{ "& th": { fontSize: "16px", fontWeight: 700 } }}>
                        <TableRow>
                            <TableCell align="center"></TableCell>
                            <TableCell align="center">Дата</TableCell>
                            <TableCell align="center">Отчет</TableCell>
                            <TableCell align="center">Форма</TableCell>
                            <TableCell align="center">Статус</TableCell>
                            <TableCell align="center"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {reports
                            ?.sort((a, b) => a.id - b.id)
                            ?.map(report => (
                                <TableRow key={report.id} sx={{ border: 0 }}>
                                    <TableCell
                                        sx={{
                                            wordWrap: "break-word",
                                            a: {
                                                color: "inherit",
                                                textDecoration: "none",
                                                display: "flex",
                                                alignItems: "center",
                                            },
                                        }}>
                                        <Checkbox disabled checked={report.state === ReportState.ACTIVE} />
                                    </TableCell>
                                    <TableCell align="center">{getDateFormat(report.report_date.expired_at)}</TableCell>
                                    <TableCell align="center">
                                        {report.report_date.name} <br />{" "}
                                        <Typography variant="caption">{report.report_date.description}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        {
                                            constants?.export_type.find(
                                                state => state.value === Number(report.report_date.report_type),
                                            )?.text
                                        }
                                    </TableCell>
                                    <TableCell align="center">
                                        {
                                            constants?.report_state.find(state => state.value === Number(report.state))
                                                ?.text
                                        }
                                        <br />{" "}
                                        <Typography variant="caption">{getDateFormat(report.updated_at)}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <div className={"btnContainer"}>
                                            {(isAdmin || isOwner) && (
                                                <>
                                                    <Button
                                                        variant="outlined"
                                                        fullWidth
                                                        component={LinkBehavior}
                                                        to={`${report.id}?report_date_id=${report.report_date_id}&report_type=${report.report_date.report_type}`}>
                                                        {isAdmin ? "Посмотреть" : "Заполнить"}
                                                    </Button>
                                                    {isOwner && (
                                                        <Button
                                                            fullWidth
                                                            variant="outlined"
                                                            onClick={() => {
                                                                setShowModal(true);
                                                                setDeleteReportId(report.id);
                                                                setCurrentModalType(MODAL_TYPE.delete);
                                                            }}>
                                                            Удалить
                                                        </Button>
                                                    )}
                                                </>
                                            )}
                                            {isAdmin && (
                                                <>
                                                    <Button
                                                        variant="outlined"
                                                        fullWidth
                                                        onClick={() => {
                                                            setShowModal(true);
                                                            setReportId(report.id);
                                                            setReportDateId(report.report_date_id);
                                                            setCurrentModalType(MODAL_TYPE.edit);
                                                        }}>
                                                        Изменить статус
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <div className={"posBR"}>
                <div className={"paginationContainer"}>
                    <Pagination
                        onChange={(index, qwerty) => setPagination({ ...pagination, current_page: qwerty })}
                        page={pagination.current_page}
                        count={pagination.last_page}
                        shape="rounded"
                    />
                </div>
                {isOwner && (
                    <Button
                        variant="outlined"
                        onClick={() => {
                            createForm.reset();
                            setCurrentModalType(MODAL_TYPE.create);
                            setShowModal(true);
                        }}>
                        Добавить
                    </Button>
                )}
            </div>
            <Modal
                open={showModal}
                title={modalType[currentModalType]?.title}
                onClose={() => {
                    closeModal();
                }}
                onSubmit={modalType[currentModalType]?.onSubmit}>
                {modalType[currentModalType]?.content}
            </Modal>
            <CustomSnackbar openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} notification={notification} />
            <Loader backDrop open={load} />
        </div>
    );
};
